import { Component, inject, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { User } from '@adgate/modules/admin/types/user.type';
import { CommercialOffice } from '@adgate/common/types/commercial-office.type';
import { Agency } from '@adgate/common/types/agency.type';
import { StateService } from '@adgate/common/services/state-service';
import { AdminService } from '@adgate/modules/admin/services/admin-service';
import { forkJoin } from 'rxjs';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CustomValidators } from '@adgate/common/validators/custom-validators.validator';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import { Role } from '@adgate/modules/admin/types/role.type';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-user-edit-dialog-component',
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatTableModule,
    MatSlideToggleModule,
    MatSortModule,
    ObjectDescPipe,
    ReactiveFormsModule
],
  templateUrl: './user-edit-dialog-component.html',
  styleUrl: './user-edit-dialog-component.scss',
})
export class UserEditDialogComponent implements OnInit, AfterViewInit
{
  @ViewChild('rolesSort') sort!: MatSort;

  private dialogRef: MatDialogRef<UserEditDialogComponent> = inject(MatDialogRef);
  private fb: FormBuilder = inject(FormBuilder);
  public stateService: StateService = inject(StateService);
  public authService: AuthenticationService = inject(AuthenticationService);
  public adminService: AdminService = inject(AdminService);
  public dialogService: DialogService = inject(DialogService);
  public comparatorService: ComparatorService = inject(ComparatorService);
  public data: User = inject(MAT_DIALOG_DATA, { optional: false });

  public offices: CommercialOffice[] = [];
  public agencies: Agency[] = [];
  public activeTab: number = 0;
  public userForm: FormGroup;

  public rolesDisplayedColumns: string[] = ['select', 'role'];
  public rolesDataSource: MatTableDataSource<Role> = new MatTableDataSource<Role>();
  public roleSelection = new SelectionModel<Role>(true, []);

  constructor()
  {
    this.userForm = this.fb.group({
      userName: ['', [Validators.required, CustomValidators.invalidCharactersValidator()]],
      email: ['', [Validators.required, Validators.email]],
      authType: ['', [Validators.required]],
      password: ['', [Validators.required,Validators.minLength(8)]],
      changePasswordOnNextLogin: [false],
      generateAndEmailPassword: [false],
      accountLocked: [false],
      userType: [3],
      office: [{value: null, disabled: true}, [Validators.required]],
      agency: [{value: null, disabled: true}, [Validators.required]]
    });

    this.userForm.get('password')?.setValidators([Validators.required,Validators.minLength(8),CustomValidators.passwordComplexityValidator(this.userForm.get('email')?.value)]);
  }

  ngOnInit(): void
  {
    let attr = this.authService.getUserAttributes();

    forkJoin([
      this.adminService.getCommercialOffices(),
      this.adminService.getAgencies(),
      this.adminService.getRoles(attr.type == 1)

    ]).subscribe({
      next: (responses:RESTApplicationResponse[]) => {
        this.offices = responses[0].data;
        this.agencies = responses[1].data;
        this.rolesDataSource.data = responses[2].data;

        if( this.data?.id != -1 ) {
          this.userForm.get('userName')?.setValue(this.data?.fullName);
          this.userForm.get('email')?.setValue(this.data?.email);
          this.userForm.get('authType')?.setValue(this.data?.authMethod);
          this.userForm.get('password')?.setValue('00000000000000000000');
          this.userForm.get('changePasswordOnNextLogin')?.setValue(this.data?.mustChangePassword);
          // this.userForm.get('generateAndEmailPassword')?.setValue(this.data?.mustChangePassword);
          this.userForm.get('accountLocked')?.setValue(this.data?.locked);
          this.userForm.get('userType')?.setValue(this.data?.type);
          this.userForm.get('office')?.setValue(this.data?.office);
          this.userForm.get('agency')?.setValue(this.data?.agency);

          this.data?.roles.forEach((r: Role) => {
            this.rolesDataSource.data.forEach(row => {
              if( r != null && r.id == row.id ) {
                this.roleSelection.select(row);
              }
            });
          });
        }
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  ngAfterViewInit()
  {
    this.rolesDataSource.sortingDataAccessor = (item: Role, property: string) => {
      switch(property) {
        case 'role': return item.description!.toLowerCase();
        default: return (item as any)[property];
      }
    };

    this.rolesDataSource.sort = this.sort;
  }

  onChangeUserType(userType: number): void
  {
    const officeControl = this.userForm.get('office');
    const agencyControl = this.userForm.get('agency');

    // User Type 1: Office User - enable office, disable agency
    // User Type 2: Agency Office - enable both office and agency
    // User Type 3: Multi Office/Agency - disable both

    if (userType === 1) {
      officeControl?.enable();
      agencyControl?.disable();
      agencyControl?.setValue(null);
    } else if (userType === 2) {
      officeControl?.disable();
      officeControl?.setValue(null);
      agencyControl?.enable();
    } else if (userType === 3) {
      officeControl?.disable();
      officeControl?.setValue(null);
      agencyControl?.disable();
      agencyControl?.setValue(null);
    }
  }

  onCancel(): void
  {
    this.dialogRef.close(null);
  }

  onSubmit(): void
  {
    if( this.userForm.valid ) {
      this.data.fullName = this.userForm.get('userName')?.value;
      this.data.email = this.userForm.get('email')?.value;
      this.data.authMethod = this.userForm.get('authType')?.value;
      this.data.password = this.userForm.get('password')?.value;
      this.data.mustChangePassword = this.userForm.get('changePasswordOnNextLogin')?.value;
      this.data.locked = this.userForm.get('accountLocked')?.value;
      this.data.type = this.userForm.get('userType')?.value;
      this.data.office = this.userForm.get('office')?.value;
      this.data.agency = this.userForm.get('agency')?.value;
      this.data.roles = this.roleSelection.selected;

      this.dialogRef.close(this.data);
    }
  }

  masterToggleRoles()
  {
    this.isAllRolesSelected() ?
    this.roleSelection.clear() :
    this.rolesDataSource.data.forEach(row => this.roleSelection.select(row));
  }

  isAllRolesSelected()
  {
    const numSelected = this.roleSelection.selected.length;
    const numRows = this.rolesDataSource.data.length;
    return numSelected === numRows;
  }

  onRolesTabClick()
  {
    this.activeTab = 1;
    setTimeout(() => {
      if (this.sort) {
        this.sort.active = 'role';
        this.sort.direction = 'asc';
        this.rolesDataSource.sort = this.sort;
      }
    }, 0);
  }
}
